import { useForm } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

import { CoverImageField } from '@/components/admin/forms/CoverImageField';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';
import { inputClass } from '@/lib/formStyles';

const CONTEXT_LABELS: Record<string, string> = {
  news: 'Notícias',
  members: 'Membros',
  history: 'História',
  events: 'Eventos',
};

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';

interface ImageFormProps {
  contexts: string[];
  onClose: () => void;
}

export function ImageForm({ contexts, onClose }: ImageFormProps) {
  const [preview, setPreview] = useState<string | null>(null);

  const form = useForm({
    image: { context: '', alt_text: '' },
    file: null as File | null,
  });
  const { data, setData, processing } = form;
  const e = form.errors as Record<string, string | undefined>;

  form.transform((d) => ({ image: d.image, file: d.file }));

  function handleFile(file: File | null) {
    setData('file', file);
    setPreview(file ? URL.createObjectURL(file) : null);
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    trackUmamiEvent('admin_content_create', { content_type: 'image' });
    form.post('/images', { forceFormData: true, onSuccess: () => onClose() });
  }

  const contextOptions = [
    { label: 'Selecionar contexto...', value: '' },
    ...contexts.map((c) => ({ label: CONTEXT_LABELS[c] ?? c, value: c })),
  ];

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <span className={labelClass}>
          Imagem <span className="text-error">*</span>
        </span>
        <CoverImageField
          previewUrl={preview}
          onSelect={handleFile}
          onRemove={data.file ? () => handleFile(null) : undefined}
          fit="contain"
        />
        {e.file && <p className="text-error mt-1 text-xs">{e.file}</p>}
      </div>

      <div>
        <span className={labelClass}>
          Contexto <span className="text-error">*</span>
        </span>
        <Select
          options={contextOptions}
          value={data.image.context}
          onChange={(v) => setData('image', { ...data.image, context: v })}
          placeholder="Selecionar contexto..."
          className="w-full"
        />
        {e.context && <p className="text-error mt-1 text-xs">{e.context}</p>}
      </div>

      <div>
        <label className={labelClass} htmlFor="image-alt">
          Texto alternativo <span className="text-error">*</span>
        </label>
        <input
          id="image-alt"
          type="text"
          value={data.image.alt_text}
          onChange={(ev) => setData('image', { ...data.image, alt_text: ev.target.value })}
          required
          className={inputClass}
          placeholder="Descrição da imagem para acessibilidade"
        />
        {e.alt_text && <p className="text-error mt-1 text-xs">{e.alt_text}</p>}
      </div>

      <div className="border-line flex justify-end gap-3 border-t pt-4">
        <Button type="button" variant="white" size="sm" onClick={onClose} disabled={processing}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={processing}
          loading={processing}
        >
          {processing ? 'A guardar...' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
}
